// The admin lesson editor's API (plan R1 §2.1-§2.2) and its error sentences.
//
// The editor reads the FULL course from GET /v1/courses/admin/:id (staff only,
// drafts included, never paywalled) and changes lessons only through targeted
// writes that carry the course's `sectionsVersion` as `expectedVersion`. Every
// write answers the whole new course view, which replaces the screen state —
// the editor never sends the course back.
//
// The axios instance rejects with the response BODY (src/utils/axios.ts), so
// errors are told apart by `code`, never by HTTP status.
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { apiErrorCode, apiErrorText } from 'src/utils/apiErrorText';
import { Course, Item, Section } from 'src/@types/Course';

// ----------------------------------------------------------------------
// Requests

// Lesson keys only the server sets (R1 §2.2 #7): ids, position, Bunny media
// ids and the paywall view markers. Never sent, even when the form echoes them.
const SERVER_OWNED_LESSON_KEYS = [
  'uuid',
  'order',
  'mediaId',
  'mediaState',
  'mediaProvider',
  'assetMediaIds',
  '_id',
  'id',
  'locked',
  'entitled',
  'lockedItemCount',
  'accessEndsAt',
  'createdAt',
  'updatedAt',
];

const SECTION_FIELDS = ['title', 'title_ar', 'description', 'description_ar'] as const;
export type SectionFields = Partial<Record<typeof SECTION_FIELDS[number], string>>;

/** The lesson body of a create / edit: the form values minus server-owned keys. */
export function lessonPayload(data: Partial<Item> | Record<string, any>): Record<string, any> {
  const out: Record<string, any> = { ...(data ?? {}) };
  SERVER_OWNED_LESSON_KEYS.forEach((key) => delete out[key]);
  return out;
}

/** Only the section's text fields (never `items`, `uuid` or `order`). */
export function sectionPayload(data: Partial<Section> | Record<string, any>): SectionFields {
  const out: SectionFields = {};
  SECTION_FIELDS.forEach((key) => {
    const value = (data as any)?.[key];
    if (typeof value === 'string') out[key] = value;
  });
  return out;
}

const viewOf = (response: any): Course => response?.data?.data as Course;

export const courseLessonsApi = {
  get: (id: string) => axiosInstance.get(APIS.COURSES.ADMIN_BY_ID(id)).then(viewOf),

  reorder: (id: string, sectionIndex: number, itemUuids: string[], expectedVersion: string) =>
    axiosInstance
      .put(APIS.COURSES.ADMIN_LESSONS_ORDER(id), { sectionIndex, itemUuids, expectedVersion })
      .then(viewOf),

  addSection: (id: string, section: SectionFields, expectedVersion: string) =>
    axiosInstance.post(APIS.COURSES.ADMIN_SECTIONS(id), { section, expectedVersion }).then(viewOf),

  updateSection: (
    id: string,
    sectionIndex: number,
    section: SectionFields,
    expectedVersion: string
  ) =>
    axiosInstance
      .patch(APIS.COURSES.ADMIN_SECTION(id, sectionIndex), { section, expectedVersion })
      .then(viewOf),

  deleteSection: (id: string, sectionIndex: number, expectedVersion: string) =>
    axiosInstance
      .delete(APIS.COURSES.ADMIN_SECTION(id, sectionIndex), { params: { expectedVersion } })
      .then(viewOf),

  addLesson: (
    id: string,
    sectionIndex: number,
    lesson: Record<string, any>,
    expectedVersion: string
  ) =>
    axiosInstance
      .post(APIS.COURSES.ADMIN_SECTION_LESSONS(id, sectionIndex), { lesson, expectedVersion })
      .then(viewOf),

  updateLesson: (id: string, uuid: string, lesson: Record<string, any>, expectedVersion: string) =>
    axiosInstance
      .patch(APIS.COURSES.ADMIN_LESSON(id, uuid), { lesson, expectedVersion })
      .then(viewOf),

  deleteLesson: (id: string, uuid: string, expectedVersion: string) =>
    axiosInstance
      .delete(APIS.COURSES.ADMIN_LESSON(id, uuid), { params: { expectedVersion } })
      .then(viewOf),

  repair: (id: string, expectedVersion: string) =>
    axiosInstance.post(APIS.COURSES.ADMIN_REPAIR_LESSONS(id), { expectedVersion }).then(viewOf),
};

// ----------------------------------------------------------------------
// Errors

/**
 * What the editor does after a failed request:
 *   conflict  — someone else changed the lessons: re-read the course
 *   reload    — the list on screen is out of date (set mismatch, gone section/lesson): re-read
 *   repair    — lesson problems must be fixed first: re-read so the banner shows
 *   readOnly  — partner-owned: the editor becomes read-only
 *   needsServerUpdate — the API has no admin lesson routes yet (old backend)
 *   the rest  — show the sentence; the screen keeps (or rolls back to) the last good state
 */
export type LessonErrorKind =
  | 'conflict'
  | 'reload'
  | 'repair'
  | 'readOnly'
  | 'stripped'
  | 'invalid'
  | 'busy'
  | 'notFound'
  | 'needsServerUpdate'
  | 'network'
  | 'denied'
  | 'other';

export type LessonError = { kind: LessonErrorKind; code: string; text: string };

export const NETWORK_TEXT = 'The server could not be reached. Check the connection and try again.';
export const NEEDS_SERVER_UPDATE_TEXT =
  'This screen needs the server update: the API does not have the lesson editor yet. Ask for the latest backend to be deployed, then press Retry.';
export const OUT_OF_DATE_TEXT =
  'The lesson list on this page was out of date. The page has been reloaded; please repeat your change.';
export const CONFLICT_TEXT =
  'This course was changed in another tab or by someone else. The page has been reloaded; please repeat your change.';
export const PARTNER_TEXT =
  'This course belongs to a partner. Its lessons are managed in Partner courses.';
export const NOT_FOUND_TEXT = 'This course does not exist any more.';
export const DENIED_TEXT = 'Your account cannot change course lessons.';

const KIND_BY_CODE: Record<string, LessonErrorKind> = {
  SECTIONS_VERSION_CONFLICT: 'conflict',
  REORDER_SET_MISMATCH: 'reload',
  SECTION_NOT_FOUND: 'reload',
  LESSON_NOT_FOUND: 'reload',
  LESSONS_NEED_REPAIR: 'repair',
  COURSE_PARTNER_OWNED: 'readOnly',
  STRIPPED_LESSON_WRITE: 'stripped',
  INVALID_LESSON: 'invalid',
  INVALID_SECTIONS: 'invalid',
  COURSE_BUSY: 'busy',
  COURSE_NOT_FOUND: 'notFound',
};

const REASON_TEXT: Record<string, string> = {
  'paywall-marker': 'it is a locked (paywalled) copy',
  'paywalled-copy': 'it is a locked (paywalled) copy',
  'url-emptied': 'its video or file link would be removed',
  'questions-emptied': 'its quiz questions would be removed',
};

function lessonTitle(course: Course | undefined, uuid: string | null | undefined): string {
  if (!course || !uuid) return '';
  for (const section of course.sections ?? []) {
    const item = (section?.items ?? []).find((it) => it?.uuid === uuid);
    if (item) return item.title || item.title_ar || '';
  }
  return '';
}

function strippedText(err: any, course: Course | undefined): string {
  const lessons: any[] = Array.isArray(err?.detail?.lessons) ? err.detail.lessons : [];
  const named = lessons.map((l) => {
    const title = lessonTitle(course, l?.uuid);
    const who = title ? `"${title}"` : 'a lesson';
    const reasons = (Array.isArray(l?.reasons) ? l.reasons : [])
      .map((r: string) => REASON_TEXT[r] || r)
      .join(' and ');
    return reasons ? `${who}: ${reasons}` : who;
  });
  const head = 'Not saved: this would remove lesson videos, files or quiz questions';
  return named.length
    ? `${head} (${named.join('; ')}). Reload the page and try again.`
    : `${head}. Reload the page and try again.`;
}

const isRouteMissing = (err: any) =>
  !apiErrorCode(err) &&
  typeof err?.message === 'string' &&
  /^Cannot (GET|POST|PUT|PATCH|DELETE) \S*\/courses\/admin\//.test(err.message);

const isNetwork = (err: any) =>
  !err ||
  err === 'Something went wrong' ||
  err === 'Network Error' ||
  err?.message === 'Network Error';

/** The kind and the readable sentence of a failed lesson-editor request. */
export function lessonErrorOf(err: any, course?: Course): LessonError {
  const code = apiErrorCode(err);
  if (typeof err === 'string' || !err) {
    return {
      kind: isNetwork(err) ? 'network' : 'other',
      code,
      text: isNetwork(err) ? NETWORK_TEXT : err,
    };
  }
  if (isRouteMissing(err))
    return { kind: 'needsServerUpdate', code, text: NEEDS_SERVER_UPDATE_TEXT };
  if (isNetwork(err)) return { kind: 'network', code, text: NETWORK_TEXT };
  const kind: LessonErrorKind =
    KIND_BY_CODE[code] ||
    (err?.error === 'ValidationExceptions' ? 'invalid' : '') ||
    (err?.error === 'ForbiddenException' ? 'denied' : 'other');
  switch (kind) {
    case 'conflict':
      return { kind, code, text: CONFLICT_TEXT };
    case 'reload':
      return { kind, code, text: OUT_OF_DATE_TEXT };
    case 'readOnly':
      return { kind, code, text: PARTNER_TEXT };
    case 'stripped':
      return { kind, code, text: strippedText(err, course) };
    case 'notFound':
      return { kind, code, text: NOT_FOUND_TEXT };
    case 'denied':
      return { kind, code, text: DENIED_TEXT };
    default:
      return { kind, code, text: apiErrorText(err, 'The change was not saved.') };
  }
}

/** Whether the editor should re-read the course after this error. */
export const shouldReload = (e: LessonError) =>
  e.kind === 'conflict' || e.kind === 'reload' || e.kind === 'repair' || e.kind === 'readOnly';
